import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, ChevronLeft, ChevronRight, Search, Plus } from 'lucide-react';
import ThemeChanger from '../Components/ThemeChanger';
import api from '../api';

function capitalize(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export default function AdminTopics() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subtopicsLoading, setSubtopicsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [topics, setTopics] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [topicFilter, setTopicFilter] = useState('all');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [openModules, setOpenModules] = useState(null); // { subtopicId, loading, modules }

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10); // Number of rows per page

  // Fetch topics only
  useEffect(() => {
    const fetchTopics = async () => {
      setLoading(true);
      try {
        // Get topics
        const topicsResponse = await api.get('/admin/get-all-topics');
        if (topicsResponse.data.statusCode !== 200) {
          throw new Error('Failed to fetch topics');
        }
        const topicsList = topicsResponse.data.data || [];
        setTopics(topicsList);

        // Initially load all subtopics
        fetchSubtopicsForTopic('all');
      } catch (err) {
        console.error('Error fetching topics:', err);
        setError('Failed to load topics. Please try again.');
        setLoading(false);
      }
    };

    fetchTopics();
  }, []);

  // Fetch subtopics for selected topic
  const fetchSubtopicsForTopic = async (topicId) => {
    setSubtopicsLoading(true);
    try {
      let endpoint = '/admin/get-all-subtopics';
      if (topicId !== 'all') {
        endpoint = `/admin/get-all-subtopics?topicId=${topicId}`;
      }

      const subtopicsResponse = await api.get(endpoint);
      if (subtopicsResponse.data.statusCode !== 200) {
        throw new Error('Failed to fetch subtopics');
      }

      // The backend now returns an array of subtopics with _id, name, topicName
      const subtopicsList = subtopicsResponse.data.data || [];

      // Use the response as-is for table display
      const flatData = subtopicsList.map((subtopic) => ({
        subtopicId: subtopic._id,
        subtopicName: subtopic.name,
        topicName: subtopic.topicName,
      }));

      setTableData(flatData);
    } catch (err) {
      console.error('Error fetching subtopics:', err);
      setError('Failed to load subtopics. Please try again.');
    } finally {
      setSubtopicsLoading(false);
      setLoading(false);
    }
  };

  // Filter data based on search term only (topic filtering is done via API)
  const filteredData = tableData.filter((row) => {
    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (
        row.topicName.toLowerCase().includes(term) ||
        row.subtopicName.toLowerCase().includes(term)
      );
    }
    return true;
  });

  // Get current items for pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const nextPage = () =>
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  const prevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));

  // Handle topic filter change - Make API request for each topic selection
  const handleTopicChange = (e) => {
    const selectedTopicId = e.target.value;
    setTopicFilter(selectedTopicId);
    setCurrentPage(1); // Reset to first page on filter change
    fetchSubtopicsForTopic(selectedTopicId);
  };

  // Handle search
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page on search
  };

  // Handle delete subtopic
  const handleDeleteClick = (subtopicId) => {
    setConfirmDelete(subtopicId);
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;

    setIsDeleting(true);
    try {
      const response = await api.delete(
        `/admin/delete-subtopic/${confirmDelete}`
      );
      if (response.data.statusCode === 200) {
        // Remove deleted subtopic from table data
        setTableData((prevData) =>
          prevData.filter((row) => row.subtopicId !== confirmDelete)
        );
      } else {
        console.error('Failed to delete subtopic:', response.data.message);
      }
    } catch (error) {
      console.error('Error deleting subtopic:', error);
    } finally {
      setConfirmDelete(null);
      setIsDeleting(false);
    }
  };

  // Function to open modules modal and fetch data
  const handleOpenModules = async (subtopicId) => {
    setOpenModules({ subtopicId, loading: true, modules: [] });
    try {
      const response = await api.get(
        `/admin/get-modules?subTopicId=${subtopicId}`
      );
      // The backend returns { data: [...] }
      const resData = response.data.data || [];
      setOpenModules({ subtopicId, loading: false, modules: resData });
    } catch (err) {
      setOpenModules({
        subtopicId,
        loading: false,
        modules: [],
        error: 'Failed to load modules.',
      });
    }
  };

  return (
    <div className="min-h-screen bg-base-200 p-4">
      {/* Header with navigation and action buttons */}
      <div className="fixed top-0 left-0 right-0 z-10 px-4 py-3 bg-base-100 shadow-md">
        <div className="w-full max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-2">
            <button
              className="btn btn-info btn-outline btn-sm"
              onClick={() => navigate('/')}
            >
              Home
            </button>
            <button
              className="btn btn-warning btn-outline btn-sm"
              onClick={() => navigate('/admin')}
            >
              Dashboard
            </button>
          </div>
          <div className="flex items-center gap-3">
            <button
              className="btn btn-primary btn-sm"
              onClick={() => navigate('/admin')}
            >
              <Plus size={16} className="mr-1" />
              Add Topic/Subtopic
            </button>
            <ThemeChanger />
          </div>
        </div>
      </div>

      {/* Main content with padding for fixed header */}
      <div className="pt-16 max-w-7xl mx-auto">
        <h1 className="text-2xl font-bold mb-6">
          Topics & Subtopics Management
        </h1>

        {/* Filter and search controls */}
        <div className="flex flex-col md:flex-row justify-between gap-4 mb-6">
          <div className="w-full md:w-auto">
            <select
              className="select select-bordered w-full md:w-64"
              value={topicFilter}
              onChange={handleTopicChange}
              disabled={loading}
            >
              <option value="all">All Topics</option>
              {topics.map((topic) => (
                <option key={topic._id} value={topic._id}>
                  {topic.name}
                </option>
              ))}
            </select>
          </div>

          <div className="relative w-full md:w-auto">
            <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
              <Search size={18} className="text-base-content/50" />
            </div>
            <input
              type="text"
              placeholder="Search topics or subtopics..."
              className="input input-bordered pl-10 w-full md:w-72"
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
        </div>

        {/* Loading state */}
        {(loading || subtopicsLoading) && (
          <div className="flex justify-center items-center py-20">
            <div className="flex flex-col items-center gap-4">
              <span className="loading loading-spinner loading-lg text-primary"></span>
              <p className="text-base-content/70">
                {loading ? 'Loading topics...' : 'Loading subtopics...'}
              </p>
            </div>
          </div>
        )}

        {/* Error state */}
        {error && !loading && !subtopicsLoading && (
          <div className="alert alert-error mb-6">
            <div>
              <span>{error}</span>
            </div>
          </div>
        )}

        {/* Table View */}
        {!loading && !subtopicsLoading && !error && (
          <div className="bg-base-100 rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="table table-zebra w-full">
                <thead>
                  <tr>
                    <th>Topic Name</th>
                    <th>Subtopic</th>
                    <th className="w-24 text-center">Actions</th>
                    <th className="w-24 text-center">Open</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.length > 0 ? (
                    currentItems.map((row) => (
                      <tr key={row.subtopicId} className="hover">
                        <td>{row.topicName}</td>
                        <td>{row.subtopicName}</td>
                        <td className="text-center">
                          <button
                            className="btn btn-sm btn-error btn-outline"
                            onClick={() => {
                              handleDeleteClick(row.subtopicId);
                            }}
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                        <td className="text-center">
                          <button
                            className="btn btn-sm btn-info btn-outline"
                            onClick={() => handleOpenModules(row.subtopicId)}
                          >
                            Open
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center py-8">
                        {searchTerm
                          ? 'No results match your search. Try different keywords.'
                          : 'No subtopics found for the selected topic.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pagination */}
        {!loading && !subtopicsLoading && !error && totalPages > 1 && (
          <div className="flex justify-center mt-6">
            <div className="join">
              <button
                className="join-item btn"
                onClick={prevPage}
                disabled={currentPage === 1}
              >
                <ChevronLeft />
              </button>

              {[...Array(totalPages)].map((_, index) => {
                // Show limited page numbers with ellipsis for better UI
                const pageNum = index + 1;
                if (
                  pageNum === 1 ||
                  pageNum === totalPages ||
                  (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                ) {
                  return (
                    <button
                      key={index}
                      className={`join-item btn ${currentPage === pageNum ? 'btn-active' : ''}`}
                      onClick={() => paginate(pageNum)}
                    >
                      {pageNum}
                    </button>
                  );
                } else if (
                  (pageNum === 2 && currentPage > 3) ||
                  (pageNum === totalPages - 1 && currentPage < totalPages - 2)
                ) {
                  return (
                    <button key={index} className="join-item btn btn-disabled">
                      ...
                    </button>
                  );
                } else {
                  return null;
                }
              })}

              <button
                className="join-item btn"
                onClick={nextPage}
                disabled={currentPage === totalPages}
              >
                <ChevronRight />
              </button>
            </div>
          </div>
        )}

        {/* Table info */}
        {!loading && !subtopicsLoading && !error && filteredData.length > 0 && (
          <div className="text-center mt-3 text-sm text-base-content/70">
            Showing {indexOfFirstItem + 1} to{' '}
            {Math.min(indexOfLastItem, filteredData.length)} of{' '}
            {filteredData.length} entries
          </div>
        )}
      </div>

      {/* Delete confirmation modal */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center">
          <div className="bg-base-100 p-6 rounded-lg shadow-xl max-w-md w-full">
            <h3 className="font-bold text-xl mb-4">Confirm Deletion</h3>
            <p className="mb-6 text-base-content/80">
              Are you sure you want to delete this subtopic? This action cannot
              be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                className="btn btn-outline"
                onClick={() => setConfirmDelete(null)}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                className="btn btn-error"
                onClick={handleConfirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <span className="loading loading-spinner loading-sm"></span>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modules Modal */}
      {openModules && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center">
          <div className="bg-base-100 p-6 rounded-lg shadow-xl max-w-md w-full relative">
            <h3 className="font-bold text-xl mb-4">Modules</h3>
            {openModules.loading ? (
              <div className="flex justify-center items-center py-8">
                <span className="loading loading-spinner loading-lg text-primary"></span>
              </div>
            ) : openModules.error ? (
              <div className="alert alert-error mb-4">
                <span>{openModules.error}</span>
              </div>
            ) : openModules.modules.length === 0 ? (
              <div className="text-center text-base-content/70 py-8">
                No modules found for this subtopic.
              </div>
            ) : (
              <table className="table w-full">
                <thead>
                  <tr>
                    <th>Module</th>
                    <th>Type</th>
                  </tr>
                </thead>
                <tbody>
                  {openModules.modules.map((mod, idx) => (
                    <tr key={idx}>
                      <td>{mod.module}</td>
                      <td>{mod.type}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div className="flex justify-end mt-6">
              <button
                className="btn btn-outline"
                onClick={() => setOpenModules(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
